import React from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import {
  Switch,
  FormControlLabel,
  Grid,
  Radio,
  Paper,
  Typography,
} from "@mui/material"
import TextFieldNumber from "common/TextFieldNumber"
import { useMarginsDiscounts } from "hooks/margin/useMarginsDiscounts"
import { EXTRA, GENERAL, RUBRO } from "context/margin/MarginsDiscountsReducer"

const MarginTab = ({ isBudgetClosed = false }) => {
  const {
    state,
    setGeneralExpenses,
    setOverwrite,
    setOverwriteExtra,
    setIndirectExpenses,
    setIndustrialProfit,
    setAddExtraPercentage,
    setAccordionSelected,
  } = useMarginsDiscounts()
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <FormControlLabel
            checked={state.margins.accordionSelected === GENERAL}
            disabled={isBudgetClosed}
            control={
              <Radio checked={state.margins.accordionSelected === GENERAL} />
            }
            label={i18n.t("marginTab.addGeneralMargin")}
            onChange={() => {
              setAccordionSelected(GENERAL)
            }}
          />
          <Grid container item spacing={2} sx={{ mb: 2 }}>
            <Grid container item spacing={2}>
              <Grid item xs={6}>
                <TextFieldNumber
                  disabled
                  label={i18n.t("marginTab.totalMargin")}
                  value={state.margins.totalMargin}
                  isPercentage
                />
              </Grid>
              <Grid
                item
                xs={6}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      disabled={
                        isBudgetClosed ||
                        state.margins.accordionSelected !== GENERAL
                      }
                      checked={state.margins.overwrite}
                      onChange={event => setOverwrite(event.target.checked)}
                      sx={{ marginRight: 1 }}
                    />
                  }
                  label={i18n.t("marginTab.overwrite")}
                  sx={{
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      verticalAlign: "middle",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextFieldNumber
                  disabled={
                    isBudgetClosed ||
                    state.margins.accordionSelected !== GENERAL
                  }
                  label={i18n.t("marginTab.generalMargin")}
                  value={state.margins.generalExpenses}
                  onChange={e => setGeneralExpenses(e)}
                  isPercentage
                  inputProps={{
                    "data-testid": "margins-modal-general-expenses-input",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextFieldNumber
                  disabled={
                    isBudgetClosed ||
                    state.margins.accordionSelected !== GENERAL
                  }
                  label={i18n.t("marginTab.indirectExpenses")}
                  value={state.margins.indirectExpenses}
                  onChange={e => setIndirectExpenses(e)}
                  isPercentage
                  inputProps={{
                    "data-testid": "margins-modal-indirect-expenses-input",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextFieldNumber
                  disabled={
                    isBudgetClosed ||
                    state.margins.accordionSelected !== GENERAL
                  }
                  label={i18n.t("marginTab.industrialProfit")}
                  value={state.margins.industrialProfit}
                  onChange={e => setIndustrialProfit(e)}
                  isPercentage
                  inputProps={{
                    "data-testid": "margins-modal-industrial-profit-input",
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <FormControlLabel
            checked={state.margins.accordionSelected === EXTRA}
            disabled={isBudgetClosed}
            control={
              <Radio checked={state.margins.accordionSelected === EXTRA} />
            }
            label={i18n.t("marginTab.addExtraPercentage")}
            onChange={() => {
              setAccordionSelected(EXTRA)
            }}
          />
          <Grid container item spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextFieldNumber
                disabled={
                  isBudgetClosed || state.margins.accordionSelected !== EXTRA
                }
                label={i18n.t("marginTab.addExtraPercentageField")}
                value={state.margins.addExtraPercentage}
                onChange={e => setAddExtraPercentage(e)}
                isPercentage
              />
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    disabled={
                      isBudgetClosed ||
                      state.margins.accordionSelected !== EXTRA
                    }
                    checked={state.margins.overwriteExtra}
                    onChange={event => setOverwriteExtra(event.target.checked)}
                    sx={{ marginRight: 1 }}
                  />
                }
                label={i18n.t("marginTab.overwrite")}
                sx={{
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    verticalAlign: "middle",
                  },
                }}
              />
            </Grid>
          </Grid>
          <FormControlLabel
            disabled
            checked={state.margins.accordionSelected === RUBRO}
            control={
              <Radio checked={state.margins.accordionSelected === RUBRO} />
            }
            label={i18n.t("marginTab.addCategoryPercentage")}
            onChange={() => {
              setAccordionSelected(RUBRO)
            }}
          />
          <Grid container item spacing={2}>
            <Grid container item spacing={2}>
              <Grid item xs={12}>
                <Grid container item spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextFieldNumber
                      disabled
                      label={i18n.t("marginTab.laborCost")}
                      value={0}
                      isPercentage
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextFieldNumber
                      disabled
                      label={i18n.t("marginTab.materials")}
                      value={0}
                      isPercentage
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextFieldNumber
                      disabled
                      label={i18n.t("marginTab.machinery")}
                      value={0}
                      isPercentage
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}

MarginTab.propTypes = {
  isBudgetClosed: PropTypes.bool,
}

export default MarginTab
